import AppOwnsDataWebApi from './AppOwnsDataWebApi';

const TOKEN_KEY = "mgmt_token";
const LOGIN_ID_KEY = "mgmt_login_id";

type Listener = (active: boolean) => void;

let listeners: Listener[] = [];

const notify = (): void => {
  const active = ManagementAuth.isActive();
  listeners.forEach((listener) => listener(active));
};

// Owns the shared-PIN "management" session: a self-issued token stored in
// sessionStorage (cleared on browser close, survives page refresh), separate
// from and unknown to MSAL.
export default class ManagementAuth {

  static subscribe(listener: Listener): () => void {
    listeners.push(listener);
    return () => { listeners = listeners.filter((l) => l !== listener); };
  }

  static getToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
  }

  static getLoginId(): string | null {
    return sessionStorage.getItem(LOGIN_ID_KEY);
  }

  static isActive(): boolean {
    return !!ManagementAuth.getToken();
  }

  static async login(pin: string): Promise<void> {
    const restUrl = AppOwnsDataWebApi.ApiRoot + "ManagementLogin/";

    const response = await fetch(restUrl, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ Pin: pin })
    });

    if (response.status === 401) {
      throw new Error("Incorrect PIN.");
    }
    if (response.status === 429) {
      const body = await response.json().catch(() => null);
      const seconds: number = body?.retryAfterSeconds ?? 0;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      const waitText = minutes > 0
        ? `${minutes}m ${remainingSeconds}s`
        : `${remainingSeconds}s`;
      throw new Error(`Too many attempts. Try again in ${waitText}.`);
    }
    if (!response.ok) {
      throw new Error("Management login is not available.");
    }

    const result = await response.json();
    sessionStorage.setItem(TOKEN_KEY, result.token);
    sessionStorage.setItem(LOGIN_ID_KEY, result.loginId);
    notify();

    await AppOwnsDataWebApi.LoginUser(result.loginId, "Management");
  }

  static logout(): void {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(LOGIN_ID_KEY);
    notify();
  }
}
